// Copyright (c) StrangeBee 2024

import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { ObservableCreate } from '@Organisms/Observable/ObservableCreate/ObservableCreate';
import { computeEventList as computeTaskEventList } from '@Organisms/Task/eventList';
import { computeEventList as computeObservableEventList } from '@Organisms/Observable/eventList';
import { ttpEventList as computeTTPEventList } from '@Organisms/TTP/eventList';
import { TaskManage } from '@Organisms/Task/TaskManage/TaskManage';
import { TTPCreate } from '@Organisms/TTP/TTPCreate/TTPCreate';
import React, { useMemo, ReactElement } from 'react';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import RouteBuilder from '@Services/RouteBuilder';
import { Link } from 'react-router-dom';
import { CaseOutput, OrgPermissionsEnum } from 'thehive-sdk';
import { classList } from '@Helpers/className';
import { ICONS } from '@Constants/iconsApp';
import { useListAllFields } from '@Queries/describeQueries';

import './CaseDrawerCount.scss';

type CountEntities = 'task' | 'observable' | 'ttp' | 'similarAlert' | 'similarCase';

const countMap: Record<
    CountEntities,
    {
        label: string;
        drawer?: (onFinish: (values: any) => Promise<any>, triggerElem: ReactElement) => ReactElement;
        link: (entities: { caseId?: string; alertId?: string }) => string;
        permission?: OrgPermissionsEnum;
    }
> = {
    task: {
        label: 'common.tasks',
        drawer: (onFinish, triggerElem) => <TaskManage onFinish={onFinish} triggerElem={triggerElem} />,
        link: ({ caseId }) => RouteBuilder.compute('cases.tasks', { caseId }),
        permission: OrgPermissionsEnum.MANAGE_TASK,
    },
    observable: {
        label: 'common.observables',
        drawer: (onFinish, triggerElem) => <ObservableCreate onFinish={onFinish} triggerElem={triggerElem} />,
        link: ({ caseId, alertId }) =>
            caseId
                ? RouteBuilder.compute('cases.observables', { caseId })
                : RouteBuilder.compute('alerts.observables', { alertId }),
        permission: OrgPermissionsEnum.MANAGE_OBSERVABLE,
    },
    ttp: {
        label: 'common.ttp',
        drawer: (onFinish, triggerElem) => <TTPCreate onFinish={onFinish} triggerElem={triggerElem} />,
        link: ({ caseId, alertId }) =>
            caseId
                ? RouteBuilder.compute('cases.procedures', { caseId })
                : RouteBuilder.compute('alerts.procedures', { alertId }),
        permission: OrgPermissionsEnum.MANAGE_PROCEDURE,
    },
    similarAlert: {
        label: 'alert.layout.nav-bar.similar-alerts',
        link: ({ caseId, alertId }) =>
            caseId
                ? RouteBuilder.compute('cases.similar-alerts', { caseId })
                : RouteBuilder.compute('alerts.similar-alerts', { alertId }),
    },
    similarCase: {
        label: 'alert.layout.nav-bar.similar-cases',
        link: ({ alertId }) => RouteBuilder.compute('alerts.similar-cases', { alertId }),
    },
};

interface CaseDrawerCountProps {
    taskCount?: number;
    obsCount: number;
    procedureCount: number;
    similarAlertsCount?: number;
    similarCasesCount?: number;
    permissions: CaseOutput['extraData']['permissions'];
    caseId?: string;
    alertId?: string;
    hasPermissions?: boolean;
    refetchKey?: string;
    onEditCounter?: () => void;
}

export const CaseDrawerCount = ({
    taskCount,
    obsCount = 0,
    procedureCount = 0,
    similarAlertsCount,
    similarCasesCount,
    permissions,
    hasPermissions = true,
    caseId,
    alertId,
    onEditCounter,
}: CaseDrawerCountProps): ReactElement => {
    const i = useLocalMessage();

    const { data: describeData } = useListAllFields();

    const counts: Record<CountEntities, number | undefined> = useMemo(
        () => ({
            task: taskCount || 0,
            observable: obsCount,
            ttp: procedureCount,
            similarAlert: similarAlertsCount,
            similarCase: similarCasesCount,
        }),
        [taskCount, obsCount, procedureCount, similarAlertsCount, similarCasesCount],
    );

    const defaultConfig = useMemo(
        () => ({
            locale: i,
            // eslint-disable-next-line
            onSuccess: (_ = false, message = '') => {
                if (message) {
                    openMessage('success', message);
                }
                onEditCounter?.();
            },
            onError: handleNotificationError,
        }),
        [i, onEditCounter],
    );

    const eventsList: Partial<Record<CountEntities, any>> = useMemo(
        () => ({
            task: computeTaskEventList({
                controller: client.TaskController,
                ...defaultConfig,
            }),
            observable: computeObservableEventList({
                controller: client.ObservableController,
                describeData,
                ...defaultConfig,
            }),
            ttp: computeTTPEventList({
                controller: client.ProcedureController,
                ...defaultConfig,
            }),
        }),
        [defaultConfig, describeData],
    );

    return (
        <div className="case-drawer-count__list">
            {Object.entries(countMap)
                .filter(([entity]) => {
                    if (taskCount === undefined && entity === 'task') {
                        return false;
                    }
                    if (similarAlertsCount === undefined && entity === 'similarAlert') {
                        return false;
                    }
                    if (similarCasesCount === undefined && entity === 'similarCase') {
                        return false;
                    }
                    return true;
                })
                .map(([entity, { drawer, label, link, permission }]) => (
                    <div key={entity} className="case-drawer-count__list-item">
                        <div
                            className={classList('case-drawer-count__list-item__title', {
                                'no-permissions':
                                    hasPermissions && (permissions as string[]).includes(permission || ''),
                            })}
                        >
                            <Link to={link({ caseId, alertId })}>{i(label)}</Link>
                            <div className="case-drawer-count__list-item__count">{counts[entity as CountEntities]}</div>
                        </div>
                        {(permissions as string[]).includes(permission || '') &&
                            hasPermissions &&
                            drawer?.(
                                (values) => eventsList[entity as CountEntities].onCreate(values, caseId || ''),
                                <THButton type="primary" block icon={<ICONS.PlusIcon />} color="gray" />,
                            )}
                    </div>
                ))}
        </div>
    );
};
